import {
  AnchorButton,
  Badge,
  Button,
  Card,
  ManageableItemsList,
  Modal,
  useToastContext,
  Text,
  H2,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { AppliancesPermissions } from '@payaca/permissions/appliances/appliances.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import useArchiveAppliance from '@/api/mutations/appliances/useArchiveAppliance';
import useGetAppliances from '@/api/queries/appliances/useGetAppliances';
import { usePermissions } from '@/hooks/usePermissions';
import { GetAppliancesQuery, ProductSpecCategory } from '@/gql/graphql';

type Appliance = GetAppliancesQuery['appliances']['items'][number];

export type AppliancesProduct = 'flow' | 'glow';

const CATEGORY_SECTIONS: ReadonlyArray<{
  product: AppliancesProduct;
  category: ProductSpecCategory;
  label: string;
  metricHeader: string;
  addLabel: string;
  addSlug: string;
}> = [
  {
    product: 'flow',
    category: 'HEAT_PUMP',
    label: 'Heat pumps',
    metricHeader: 'Output',
    addLabel: 'Add heat pump',
    addSlug: 'heat-pump',
  },
  {
    product: 'flow',
    category: 'CYLINDER',
    label: 'Water cylinders',
    metricHeader: 'Capacity',
    addLabel: 'Add cylinder',
    addSlug: 'cylinder',
  },
  {
    product: 'glow',
    category: 'SOLAR_PANEL',
    label: 'Solar panels',
    metricHeader: 'Capacity',
    addLabel: 'Add solar panel',
    addSlug: 'solar-panel',
  },
  {
    product: 'glow',
    category: 'INVERTER',
    label: 'Inverters',
    metricHeader: 'AC output',
    addLabel: 'Add inverter',
    addSlug: 'inverter',
  },
  {
    product: 'glow',
    category: 'BATTERY',
    label: 'Batteries',
    metricHeader: 'Usable capacity',
    addLabel: 'Add battery',
    addSlug: 'battery',
  },
];

const metricValue = (appliance: Appliance): string => {
  const spec = appliance.productSpec;
  switch (spec.category) {
    case 'CYLINDER':
      return spec.domesticHotWaterLitres != null
        ? `${spec.domesticHotWaterLitres} L`
        : '-';
    case 'SOLAR_PANEL':
      return spec.panelCapacityW != null ? `${spec.panelCapacityW} W` : '-';
    case 'INVERTER':
      return spec.acOutputKw != null
        ? `${spec.acOutputKw} kW${spec.hybrid ? ', hybrid' : ''}`
        : '-';
    case 'BATTERY':
      return spec.usableCapacityKwh != null
        ? `${spec.usableCapacityKwh} kWh`
        : '-';
    default:
      return spec.nominalOutputKw != null ? `${spec.nominalOutputKw} kW` : '-';
  }
};

const PreferredCheck: FC<{ appliance: Appliance }> = ({ appliance }) => {
  const archived = appliance.productSpec.archivedAt != null;
  const on = appliance.isPreferred && appliance.items.length > 0 && !archived;
  return on ? (
    <UntitledIcon
      name="check"
      className="h-5 w-5 text-green-500"
      aria-label="Preferred"
    />
  ) : (
    <span className="text-gray-300" aria-label="Not preferred">
      -
    </span>
  );
};

const NameCell: FC<{ appliance: Appliance }> = ({ appliance }) => {
  const spec = appliance.productSpec;
  const archived = spec.archivedAt != null;
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {spec.image?.url ? (
        <img
          src={spec.image.url}
          alt=""
          className="h-9 w-9 flex-shrink-0 rounded-md border border-gray-200 object-contain"
        />
      ) : (
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100 text-gray-400">
          <UntitledIcon name="box.3" className="h-4 w-4" />
        </span>
      )}
      <span className="truncate">{spec.name}</span>
      {archived && (
        <Badge variant="soft" colour="orange" rounded>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <UntitledIcon name="alert-triangle.3" className="h-3 w-3" />
            No longer available
          </span>
        </Badge>
      )}
    </div>
  );
};

const COPY: Record<AppliancesProduct, string> = {
  flow: "Set up the heat pumps and water cylinders you install as a specification, plus the catalogue items that make each one up. Mark an appliance as preferred to have it matched to the property's requirements and added to new estimates automatically.",
  glow: 'Set up the solar panels, inverters and batteries you install as a specification, plus the catalogue items that make each one up. Mark an appliance as preferred to have it matched to the roof design and added to new estimates automatically.',
};

const AppliancesSection: FC<{ product: AppliancesProduct }> = ({ product }) => {
  const history = useHistory();
  const { appliances, isLoading } = useGetAppliances();
  const { userHasRequiredPermission } = usePermissions();
  const canManage = userHasRequiredPermission([
    AppliancesPermissions.MANAGE_APPLIANCES,
  ]);
  const { pushSuccess, pushError } = useToastContext();
  const { mutateAsync: archiveAppliance, isPending: isArchiving } =
    useArchiveAppliance();
  const [toArchive, setToArchive] = useState<Appliance | null>(null);

  const all: Appliance[] = appliances ?? [];

  const onConfirmArchive = async () => {
    if (!toArchive) return;
    try {
      await archiveAppliance(toArchive.id);
      pushSuccess('Appliance deleted');
      setToArchive(null);
    } catch {
      pushError('Could not delete the appliance.');
    }
  };

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-4">
        <div>
          <H2>Appliances</H2>
          <Text>{COPY[product]}</Text>
        </div>

        {CATEGORY_SECTIONS.filter((section) => section.product === product).map(
          (section) => {
            const rows = all.filter(
              (a) => a.productSpec.category === section.category
            );
            return (
              <ManageableItemsList key={section.category}>
                <ManageableItemsList.HeaderBar
                  heading={section.label}
                  buttons={
                    canManage ? (
                      <AnchorButton
                        as={Link}
                        size="sm"
                        variant="secondary"
                        to={`/companySettings/appliances/new/${section.addSlug}`}
                      >
                        {section.addLabel}
                      </AnchorButton>
                    ) : undefined
                  }
                />
                <ManageableItemsList.Table
                  items={rows}
                  uniqueKey="id"
                  isLoading={isLoading}
                  onClickRow={
                    canManage
                      ? (appliance: Appliance) =>
                          history.push(
                            `/companySettings/appliances/edit/${appliance.id}`
                          )
                      : undefined
                  }
                  itemActions={
                    canManage
                      ? [
                          {
                            label: 'Edit',
                            onClick: (appliance: Appliance) =>
                              history.push(
                                `/companySettings/appliances/edit/${appliance.id}`
                              ),
                          },
                          {
                            label: 'Delete',
                            onClick: (appliance: Appliance) =>
                              setToArchive(appliance),
                          },
                        ]
                      : []
                  }
                  emptyText={`No ${section.label.toLowerCase()} yet. Add one to get started.`}
                >
                  <ManageableItemsList.Table.Column
                    field="productSpec"
                    header="Name"
                    className="w-full min-w-44 max-w-0"
                    render={(_productSpec, row: Appliance) => (
                      <NameCell appliance={row} />
                    )}
                  />
                  <ManageableItemsList.Table.Column
                    field="productSpec"
                    header="Model"
                    render={(productSpec: Appliance['productSpec']) =>
                      productSpec.modelNumber
                    }
                  />
                  <ManageableItemsList.Table.Column
                    field="productSpec"
                    header={section.metricHeader}
                    render={(_productSpec, row: Appliance) => metricValue(row)}
                  />
                  <ManageableItemsList.Table.Column
                    field="items"
                    header="Items"
                    render={(items: Appliance['items']) => items.length}
                  />
                  <ManageableItemsList.Table.Column
                    field="totalPrice"
                    header="Customer price"
                    render={(totalPrice: Appliance['totalPrice']) => (
                      <CurrencyValue
                        value={totalPrice.value}
                        currency={totalPrice.currency}
                      />
                    )}
                  />
                  <ManageableItemsList.Table.Column
                    field="isPreferred"
                    header="Preferred"
                    render={(_isPreferred, row: Appliance) => (
                      <PreferredCheck appliance={row} />
                    )}
                  />
                </ManageableItemsList.Table>
              </ManageableItemsList>
            );
          }
        )}

        <Modal
          isOpen={toArchive !== null}
          onClose={() => setToArchive(null)}
          title="Delete appliance"
        >
          <Modal.Body>
            Are you sure you want to delete{' '}
            <strong>{toArchive?.productSpec.name ?? 'this appliance'}</strong>?
            Estimates that already use it are unaffected.
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button variant="secondary" onClick={() => setToArchive(null)}>
                Cancel
              </Button>
              <Button
                colour="negative"
                isProcessing={isArchiving}
                onClick={() => !isArchiving && void onConfirmArchive()}
              >
                Yes, delete
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default AppliancesSection;
